import React from 'react';
import './ErrorMessage.css';

// Utility function to safely extract error message from various error types
const getErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message || error.toString();
  if (error.message) return error.message;
  // Handle Event objects specifically
  if (error instanceof Event) {
    // Try to extract error from event.error if available
    if (error.error) {
      return getErrorMessage(error.error);
    }
    // Try to get message from event
    if (error.message) return error.message;
    return 'An unexpected event occurred';
  }
  // Try toString as last resort
  const errorString = String(error);
  // If it's [object Event] or similar, return a more helpful message
  if (errorString === '[object Event]' || errorString === '[object Object]') {
    return 'An unexpected error occurred';
  }
  return errorString;
};

const ErrorMessage = ({ 
  error, 
  onRetry, 
  onDismiss, 
  onUpgradeClick,
  showRetry = true, 
  showDismiss = true,
  variant = 'error' // 'error', 'warning', 'info'
}) => {
  if (!error) return null;

  // Safely extract error message
  const errorMessage = getErrorMessage(error);

  const getIcon = () => {
    switch (variant) {
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      case 'error':
      default:
        return '';
    }
  };

  const getVariantClass = () => {
    switch (variant) {
      case 'warning':
        return 'error-message-warning';
      case 'info':
        return 'error-message-info';
      case 'error':
      default:
        return 'error-message-error';
    }
  };

  return (
    <div className={`error-message-container ${getVariantClass()}`}>
      <div className="error-message-content">
        <div className="error-message-header">
          <span className="error-icon">{getIcon()}</span>
          <span className="error-title">
            {variant === 'warning' ? 'Warning' : 
             variant === 'info' ? 'Information' : 'Error'}
          </span>
        </div>
        
        <div className="error-message-body">
          <p className="error-text" title={errorMessage}>
            {errorMessage.length > 200 ? `${errorMessage.substring(0, 200)}...` : errorMessage}
          </p>
          
          {variant === 'error' && errorMessage.includes('API key') && (
            <div className="error-help">
              <p>This appears to be a configuration issue. Please contact support if this problem persists.</p>
            </div>
          )}
        </div>

        <div className="error-message-actions">
          {showRetry && onRetry && (
            <button 
              className="error-action-button retry-button"
              onClick={onRetry}
              aria-label="Retry operation"
            >
              Retry
            </button>
          )}
          
          {onUpgradeClick && (
            <button 
              className="error-action-button upgrade-button"
              onClick={onUpgradeClick}
              aria-label="Upgrade plan"
            >
              Upgrade Plan
            </button>
          )}
          
          {showDismiss && onDismiss && (
            <button 
              className="error-action-button dismiss-button"
              onClick={onDismiss}
              aria-label="Dismiss error message"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorMessage; 